/**
 * Catalogue des permissions ServUp (RBAC).
 *
 * Source de verite partagee avec le backend (backend/utils/permissions.js).
 * Toute modification ici DOIT etre repercutee dans le fichier CJS backend.
 *
 * Pas de logique metier dans ce fichier : uniquement des constantes
 * et la fonction utilitaire `scopeIsAtLeast`.
 */

export type Scope = 'establishment' | 'organization' | 'account' | 'global';

export interface PermissionDef {
  id: string;
  category: string;
  label: string;
  description: string;
}

export interface CategoryDef {
  id: string;
  label: string;
}

export const PERMISSION_CATEGORIES: readonly CategoryDef[] = [
  { id: 'interventions', label: 'Interventions' },
  { id: 'pieces', label: 'Pièces détachées' },
  { id: 'users', label: 'Utilisateurs' },
  { id: 'technicians', label: 'Techniciens AS400' },
  { id: 'admins', label: 'Gestion des accès admin' },
  { id: 'config', label: 'Configuration métier' },
  { id: 'sync', label: 'Synchronisation IBM i' },
  { id: 'sharing', label: 'Partage des ressources' },
  { id: 'cdc', label: 'CDC Provisioning' },
  { id: 'roles', label: 'Gestion des rôles' },
  { id: 'security', label: 'Sécurité' },
  { id: 'api', label: 'API publique' },
] as const;

export const PERMISSIONS: readonly PermissionDef[] = [
  // Interventions
  {
    id: 'interventions.view',
    category: 'interventions',
    label: 'Voir les interventions',
    description: 'Liste, détail, passages, photos, paiements, PDFs',
  },
  {
    id: 'interventions.create',
    category: 'interventions',
    label: 'Créer une intervention',
    description: 'Création en mode standalone (sans IBM i)',
  },
  {
    id: 'interventions.update',
    category: 'interventions',
    label: 'Modifier une intervention',
    description: 'Statut, technicien, compte rendu, dates',
  },
  {
    id: 'interventions.archive',
    category: 'interventions',
    label: 'Archiver une intervention',
    description: 'Mettre une intervention en archive',
  },
  {
    id: 'interventions.unarchive',
    category: 'interventions',
    label: 'Désarchiver une intervention',
    description: 'Restaurer une intervention archivée',
  },

  // Pieces
  {
    id: 'pieces.view',
    category: 'pieces',
    label: 'Voir le catalogue des pièces',
    description: 'Liste, détail, statistiques, recherche',
  },
  {
    id: 'pieces.manage_mobile_visibility',
    category: 'pieces',
    label: 'Gérer la visibilité mobile',
    description: "Rendre des pièces visibles ou masquées dans l'app technicien",
  },

  // Users
  {
    id: 'users.view',
    category: 'users',
    label: 'Voir les utilisateurs',
    description: 'Liste et statistiques des comptes',
  },
  {
    id: 'users.create',
    category: 'users',
    label: 'Créer un utilisateur',
    description: 'Créer un compte (rôle limité par la hiérarchie)',
  },
  {
    id: 'users.update',
    category: 'users',
    label: 'Modifier un utilisateur',
    description: 'Modifier nom, prénom, téléphone, rôle',
  },
  {
    id: 'users.deactivate',
    category: 'users',
    label: 'Désactiver un utilisateur',
    description: "Désactivation soft delete, conserve l'historique",
  },
  {
    id: 'users.restore',
    category: 'users',
    label: 'Restaurer un utilisateur',
    description: 'Réactiver un compte précédemment désactivé',
  },

  // Technicians
  {
    id: 'technicians.view',
    category: 'technicians',
    label: 'Voir les techniciens AS400',
    description: 'Liste extraite de DBFO0, statut compte mobile',
  },
  {
    id: 'technicians.create_account',
    category: 'technicians',
    label: 'Créer un compte technicien',
    description: 'Créer un compte mobile pour un sous-traitant DBFO0',
  },
  {
    id: 'technicians.revoke_account',
    category: 'technicians',
    label: 'Révoquer un compte technicien',
    description: "Désactiver le compte mobile d'un technicien",
  },
  {
    id: 'technicians.update_email',
    category: 'technicians',
    label: "Modifier l'email d'un technicien",
    description: "Changer l'email d'un compte technicien",
  },

  // Admins
  {
    id: 'admins.view',
    category: 'admins',
    label: 'Voir les administrateurs',
    description: 'Liste des comptes administrateurs',
  },
  {
    id: 'admins.create_account',
    category: 'admins',
    label: 'Créer un compte administrateur',
    description: 'Créer un compte admin pour un utilisateur IBM i',
  },
  {
    id: 'admins.revoke',
    category: 'admins',
    label: 'Révoquer un administrateur',
    description: "Désactiver le compte admin d'un utilisateur",
  },

  // Config
  {
    id: 'config.view',
    category: 'config',
    label: 'Voir la configuration',
    description: 'Voir mapping ORETAR et statuts intervention',
  },
  {
    id: 'config.statuses.update',
    category: 'config',
    label: 'Modifier les statuts',
    description: 'Modifier libellés, couleurs, app_status, auto_archive',
  },
  {
    id: 'config.statuses.init',
    category: 'config',
    label: 'Initialiser les statuts',
    description: 'Initialiser le mapping pour un nouveau dossier',
  },
  {
    id: 'config.statuses.reset_from_ibmi',
    category: 'config',
    label: 'Réinitialiser depuis IBM i',
    description: "Resynchroniser les statuts depuis l'AS400",
  },

  // Sync
  {
    id: 'notifications.view',
    category: 'sync',
    label: "Voir le journal des envois",
    description: "Lister les courriels et SMS envoyés, avec le détail du fournisseur (les secrets en sont retirés)",
  },
  {
    id: 'sync.view_logs',
    category: 'sync',
    label: 'Voir les logs de synchronisation',
    description: "Voir sync_logs et statut de l'agent IBM i",
  },
  {
    id: 'sync.run',
    category: 'sync',
    label: 'Lancer une synchronisation',
    description: 'Sync IBM i full ou ciblée (interventions, articles, etc.)',
  },
  {
    id: 'sync.push',
    category: 'sync',
    label: 'Pousser vers IBM i (writeback)',
    description: 'Pousser explicitement les changements vers DB2',
  },

  // Sharing
  {
    id: 'sharing.view',
    category: 'sharing',
    label: 'Voir le partage des ressources',
    description: 'Voir la configuration des resource_groups',
  },
  {
    id: 'sharing.manage',
    category: 'sharing',
    label: 'Gérer le partage des ressources',
    description: 'Modifier le périmètre de partage entre établissements',
  },

  // CDC
  //
  // Le lot 8 (2026-08-20) a retire le wizard de provisionnement web. Ces deux
  // permissions lui survivent pour des raisons differentes :
  //   - cdc.view garde une route vivante, GET /api/diag/db2-stats ;
  //   - cdc.deploy COMMANDE LES CLES D'API DES AGENTS : creer (la cle en clair
  //     n'est rendue qu'une fois), revoquer, reborner l'expiration
  //     (routes/api-keys.routes.js). L'ecran disait « ecrire dans
  //     cdc_provisioning_state (heritee du wizard retire) » — une table morte,
  //     donc un droit qui semblait inoffensif : un administrateur accordait la
  //     gestion des cles sans le savoir (trouve le 19/09/2026).
  //     Elle subsiste AUSSI dans la policy RLS d'ecriture sur
  //     cdc_provisioning_state (migration 20260503011650) : surface residuelle,
  //     a trancher avec le sort de la table.
  {
    id: 'cdc.view',
    category: 'cdc',
    label: "Voir l'état CDC",
    description: "Lister les clés d'API des agents IBM i, sans leur empreinte (GET /api/api-keys)",
  },
  {
    id: 'cdc.deploy',
    category: 'cdc',
    label: 'Déployer le CDC',
    description: "Créer, révoquer ou reborner une clé d'API d'agent (POST/PATCH /api/api-keys)",
  },

  // Roles
  {
    id: 'roles.manage',
    category: 'roles',
    label: 'Gérer les rôles et permissions',
    description: 'Accéder à la page de gestion des rôles (super_admin)',
  },

  // Sécurité
  {
    id: 'security.manage',
    category: 'security',
    label: 'Gérer la sécurité du compte',
    description: 'Exiger la double authentification des techniciens du compte',
  },

  // API publique v1 (décision du 17/09 : les admins d'un compte délivrent les clés)
  {
    id: 'api.manage',
    category: 'api',
    label: "Gérer les clés de l'API publique",
    description: "Créer, lister et révoquer les clés api.read / api.ingest / api.changes des établissements de son périmètre (une clé api.ingest écrit dans les données de l'organisation). Les clés d'agent IBM i restent à cdc.deploy.",
  },
] as const;

export const SCOPE_ORDER: Record<Scope, number> = {
  establishment: 1,
  organization: 2,
  account: 3,
  global: 4,
};

export const SCOPE_LABELS: Record<Scope, string> = {
  establishment: 'Établissement',
  organization: 'Organisation',
  account: 'Compte',
  global: 'Global',
};

/**
 * Retourne true si le scope `actual` est au moins aussi large que `required`.
 * Retourne false si `actual` est invalide (inconnu, null, undefined).
 */
export function scopeIsAtLeast(
  actual: Scope | string | null | undefined,
  required: Scope
): boolean {
  if (actual === null || actual === undefined) {
    return false;
  }
  const a = (SCOPE_ORDER as Record<string, number>)[actual];
  const r = SCOPE_ORDER[required];
  if (typeof a !== 'number' || typeof r !== 'number') {
    return false;
  }
  return a >= r;
}
